// src/components/HomePage.js
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Card, Accordion, Form } from 'react-bootstrap'; // Added Accordion and Form
import { NavLink, useNavigate } from 'react-router-dom';
import { Fade, Slide, Zoom } from 'react-awesome-reveal';
// Update your icon imports to include:
import { 
  FaAward, FaStar, FaCheckCircle, FaLaptopCode, FaRegLightbulb, 
  FaCoffee, FaSmile, FaUsers, FaCode, FaMobileAlt, FaBullhorn, 
  FaPaintBrush, FaQuoteRight, FaPencilRuler, FaTasks, FaGlobe, 
  FaAndroid, FaDesktop, FaCog, FaLightbulb, FaTools, FaChevronRight ,FaRocket,FaChartLine,FaGraduationCap, FaHeartbeat, FaTheaterMasks, FaCoins, FaShoppingBag,
  FaPlane, FaTruck, FaUtensils, FaHome, FaIndustry
} from 'react-icons/fa';

const HomePage = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const navigate = useNavigate();
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
 const RECAPTCHA_SITE_KEY = isLocalhost
  ? null
  : (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcvAwEtAAAAAOzRSm46-IXbt_xxAlStAD5zYorx');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      setIsRecaptchaReady(true);
      return;
    }

    const scriptId = 'recaptcha-v3-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => setIsRecaptchaReady(true));
        }
      };
      document.body.appendChild(script);
    } else if (window.grecaptcha) {
      window.grecaptcha.ready(() => setIsRecaptchaReady(true));
    }
  }, [RECAPTCHA_SITE_KEY]);

  const executeRecaptcha = async (action) => {
    if (!RECAPTCHA_SITE_KEY) {
      return 'dev-localhost-bypass';
    }
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA is not loaded.');
    }
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  };

  const mailEndpoint = process.env.NODE_ENV === 'development' ? '/api/sendmail' : '/sendmail.php';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(false);
  };

  const sendHomeForm = async (captchaToken) => {
    try {
      const response = await fetch(mailEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          phone: formData.phone,
          message: formData.message,
          captchaToken: captchaToken
        })
      });
      
      const responseText = await response.text();
      if (!response.ok) {
        console.error('Non-OK response body:', responseText);
        throw new Error(`HTTP error! status: ${response.status} body: ${responseText}`);
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (err) {
        console.error('Invalid JSON response from', mailEndpoint, responseText);
        throw new Error('Invalid JSON response: ' + responseText);
      }
      if (result.success) {
        setSuccessMessage('Message sent successfully!');
        setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
        setTimeout(() => navigate('/thank-you'), 1500);
      } else {
        setErrorMessage('Error sending message: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Form error:', error);
      setErrorMessage('Error sending message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    if (!isRecaptchaReady) {
      setErrorMessage('reCAPTCHA is not ready yet. Please wait a moment and try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const captchaToken = await executeRecaptcha('contact_form');
      await sendHomeForm(captchaToken);
    } catch (error) {
      console.error('Submit error:', error);
      setErrorMessage('Error sending message. Please try again.');
      setIsSubmitting(false);
    }
  };

  const heroBackground = isMobile
    ? `${process.env.PUBLIC_URL}/assets/banner/banner-mobile.jpg`
    : `${process.env.PUBLIC_URL}/assets/banner/banner-1.jpg`;

  return (
    <div>
      <section className="hero-section text-center text-white" style={{
        backgroundImage: `url(${heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        zIndex: 1
      }}>
         
        {/* Overlay is handled by ::before in index.css */}
        <Container className="position-relative" style={{ zIndex: 2 }}>
          <Row>
            <Col lg={10} className="mx-auto">
              <Fade triggerOnce direction="down" duration={1200}>
                <span className="text-divider d-block mb-3">Welcome to Webbloom </span>
                <h1 className="fw-bolder mb-4">
                  Building Websites That Build Your Business
                </h1>
              </Fade>
              <Fade triggerOnce delay={300} duration={1200}>
                <p className="lead mb-5">
                We build modern, high-performing websites that look great, work seamlessly, and help your business grow.
                </p>
              </Fade>
              <div className="d-flex justify-content-center">
                <Slide triggerOnce direction="up" duration={1000} delay={600}>
                  <Button as={NavLink} to="/about-us" variant="primary" size="lg" className="me-3">Explore now</Button>
                  <Button as={NavLink} to="/contact-us" variant="outline-light" size="lg">Contact now</Button>
                </Slide>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== 2. Feature Boxes Section ==================== */}
      <section className="bg-light-amiso">
        <Container>
          <Row className="justify-content-center g-4">
            {/* Feature Box 1 */}
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={100}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaCode size={45} />
                  </div>
                  <h5 className="fw-bold text-white mb-2">Innovative Approach</h5>
                  <p className="text-muted mb-0">We bring fresh ideas and modern solutions to every project.</p>
                </div>
              </Zoom>
            </Col>

            {/* Feature Box 2 */}
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={200}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaRocket size={45} />
                  </div>
                  <h5 className="fw-bold text-white mb-2">High-Quality Development</h5>
                  <p className="text-muted mb-0">We create fast, secure, and scalable digital products for long-term performance.</p>
                </div>
              </Zoom>
            </Col>

            {/* Feature Box 3 */}
            <Col lg={4} md={6}>
              <Zoom triggerOnce delay={300}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaCheckCircle size={45} />
                  </div>
                  <h5 className="fw-bold text-white mb-2">Client-Focused Results</h5>
                  <p className="text-muted mb-0">Your growth is our goal — we deliver real business impact.</p>
                </div>
              </Zoom>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== 3. About Section (Experienced designers & developers) ==================== */}
      <section className="bg-dark">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <Slide triggerOnce direction="left">
                <div className="position-relative">
                  <img src="/assets/about-us/about.jpg" alt="Webbloom" className="img-fluid rounded-3 shadow-lg" />
                  {/* <div className="position-absolute d-flex flex-column align-items-center justify-content-center text-white p-3 rounded-circle" 
                       style={{ bottom: '20px', left: '20px', backgroundColor: 'var(--thm-base)', width: '120px', height: '120px', fontWeight: 'bold' }}>
                    <span className="fs-3">Founded</span>
                    <span className="fs-4">1966</span>
                  </div> */}
                </div>
              </Slide>
            </Col>
            <Col lg={6}>
              <Slide triggerOnce direction="right">
                <span className="text-divider d-block mb-3">About Us</span>
                <h2 className="section-title text-white mb-4">
                 We build websites that grow your business
                </h2>
                <p className="section-subtitle text-muted mb-4">
                 Webbloom is a modern web development company focused on creating powerful, scalable, and visually engaging digital solutions. We specialize in HTML websites, WordPress development, custom web applications, mobile apps, and SEO services tailored to help businesses grow online.
                </p>
                
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="d-flex align-items-center mb-3">
                      <FaLaptopCode size={30} className=" me-3" />
                      <div>
                        <h5 className="fw-bold text-white mb-1">Web solutions</h5>
                        <p className="text-muted small mb-0">Smart, scalable websites built for performance and growth.</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center mb-3">
                      <FaRegLightbulb size={30} className=" me-3" />
                      <div>
                        <h5 className="fw-bold text-white mb-1">Best interface</h5>
                        <p className="text-muted small mb-0">Clean, modern designs focused on user experience.</p>
                      </div>
                    </div>
                  </Col>
                </Row>
                 <Button as={NavLink} to="/about-us" variant="primary" size="lg">Explore now</Button>
              </Slide>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* ==================== 4. Counter Section ==================== */}
      <section className="bg-light-amiso text-center py-5">
        <Container>
          <Fade triggerOnce direction="down">
            <span className="text-divider d-block mb-3">Happy Cutomers</span>
            <h2 className="section-title  mb-4">Growing With Every Project</h2> {/* Large number as in Amiso */}
          </Fade>
          
          <Row className="justify-content-center mt-5">
            {/* Counter Item 1 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={100} className='counter-grid'>
                <div className="text-white">
                  <FaLaptopCode size={50} className="counter-icon mb-3" />
                  <h3 className="fw-bold text-white mb-1">63</h3>
                  <p className="text-muted mb-0">Projects completed</p>
                </div>
              </Zoom>
            </Col>

            {/* Counter Item 2 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={200} className='counter-grid'>
                <div className="text-white">
                  <FaUsers size={50} className="counter-icon mb-3" />
                  <h3 className="fw-bold text-white mb-1">10+</h3>
                  <p className="text-muted mb-0">Active clients</p>
                </div>
              </Zoom>
            </Col>

            {/* Counter Item 3 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={300} className='counter-grid'>
                <div className="text-white">
                  <FaAward size={50} className="counter-icon mb-3" />
                  <h3 className="fw-bold text-white mb-1">3+</h3>
                  <p className="text-muted mb-0">Years Of Experience</p>
                </div>
              </Zoom>
            </Col>

            {/* Counter Item 4 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={400} className='counter-grid'>
                <div className="text-white">
                  <FaSmile size={50} className="counter-icon mb-3" />
                  <h3 className="fw-bold text-white mb-1">49</h3>
                  <p className="text-muted mb-0">Satisfied customers</p>
                </div>
              </Zoom>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== 5. Services Grid Section ==================== */}
      <section className="bg-dark">
        <Container>
          <Row className="align-items-center mb-5">
            <Col lg={8} className="text-center text-lg-start">
              <Fade triggerOnce direction="left">
                <span className="text-divider d-block mb-3">What We're Offering</span>
                <h2 className="section-title text-white">
                  Services we're offering to <br /> customer to grow business
                </h2>
              </Fade>
            </Col>
            <Col lg={4} className="text-center text-lg-end mt-4 mt-lg-0">
               <Fade triggerOnce delay={500}>
            <div className="text-center mt-5">
              {/* <p className="text-muted mb-4">Start your journey with high-quality web and app development. We design, build, and grow your digital presence.</p> */}
              <Button as={NavLink} to="/services" variant="primary" size="lg">View All Services</Button>
            </div>
          </Fade>
            </Col>
          </Row>

          <Row className="justify-content-center">
            {/* Service Card 1 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={100}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaCode size={50} /> {/* Web development icon */}
                  </div>
                  <h5 className="fw-bold text-white mb-2">Web development</h5>
                  <p className="text-muted small mb-0">Modern and responsive websites built for speed, performance, and seamless user experience.</p>
                </div>
              </Zoom>
            </Col>

            {/* Service Card 2 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={200}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaMobileAlt size={50} /> {/* Web application icon */}
                  </div>
                  <h5 className="fw-bold text-white mb-2">Web application</h5>
                  <p className="text-muted small mb-0">Scalable web apps designed to simplify processes and deliver powerful functionality.</p>
                </div>
              </Zoom>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={400}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaUsers size={50} /> {/* Web designing icon */}
                  </div>
                  <h5 className="fw-bold text-white mb-2">CRM</h5>
                  <p className="text-muted small mb-0">Smart customer management systems to organize, track, and grow your business relationships.</p>
                </div>
              </Zoom>
            </Col>
            {/* Service Card 3 */}
            <Col lg={3} md={6} className="mb-4">
              <Zoom triggerOnce delay={300}>
                <div className="amiso-card text-center p-4">
                  <div className=" mb-3">
                    <FaChartLine size={50} /> {/* Web marketing icon */}
                  </div>
                  <h5 className="fw-bold text-white mb-2">CRM Solutions</h5>
                  <p className="text-muted small mb-0">Streamline customer relationships, improve engagement, and grow your business efficiently.</p>
                </div>
              </Zoom>
            </Col>

            {/* Service Card 4 */}
           
          </Row>

         
        </Container>
      </section>
{/* ==================== 9. IT Services Section ==================== */}
      <section style={{
        backgroundImage: 'url("assets/images/backgrounds/it-services-bg.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="overlay" style={{ background: 'rgba(0,0,0,0.85)', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}></div>
        <Container className="position-relative" style={{ zIndex: 2 }}>
          <Row className="align-items-center">
            <Col lg={12} className="text-center text-lg-center mb-5 mb-lg-0">
              <Fade triggerOnce direction="left">
                <span className="text-divider d-block mb-3">Industry We Serve</span>
                <h2 className="section-title text-white mb-5">
                 We Provide Services For <br/> Following Industries
                </h2>
                {/* <p className="section-subtitle text-muted mb-5">
                  Lorem ipsum dolor sit amet, consectetur notted adipisicing elit sed do eiusmod tempor incididunt ut labore.
                </p>
                <Button variant="primary" size="lg">Explore now</Button> */}
              </Fade>
            </Col>
            <Col lg={12}>
              <Row className="g-4">

                <Col md={2}>
                  <Zoom triggerOnce delay={100}>
                    <div className="amiso-card p-4 text-center">
                      <FaGraduationCap size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Education</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={200}>
                    <div className="amiso-card p-4 text-center">
                      <FaHeartbeat size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Health Care</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={300}>
                    <div className="amiso-card p-4 text-center">
                      <FaTheaterMasks size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Entertainment</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={400}>
                    <div className="amiso-card p-4 text-center">
                      <FaCoins size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Financial Services</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={500}>
                    <div className="amiso-card p-4 text-center">
                      <FaUsers size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Business</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={600}>
                    <div className="amiso-card p-4 text-center">
                      <FaShoppingBag size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">E-commerce</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={700}>
                    <div className="amiso-card p-4 text-center">
                      <FaPlane size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Tourism</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={800}>
                    <div className="amiso-card p-4 text-center">
                      <FaTruck size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Logistics</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={900}>
                    <div className="amiso-card p-4 text-center">
                      <FaUtensils size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Restaurants</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={1000}>
                    <div className="amiso-card p-4 text-center">
                      <FaHome size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Real Estate</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={1100}>
                    <div className="amiso-card p-4 text-center">
                      <FaIndustry size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">Industry Oil & Gas</h6>
                    </div>
                  </Zoom>
                </Col>

                <Col md={2}>
                  <Zoom triggerOnce delay={1200}>
                    <div className="amiso-card p-4 text-center">
                      <FaLaptopCode size={40} className="industry-icon mb-3" />
                      <h6 className="fw-bold text-white mb-0">IT</h6>
                    </div>
                  </Zoom>
                </Col>

              </Row>
            </Col>
          </Row>
        </Container>
      </section>
      {/* ==================== 6. Testimonial Section (Static) ==================== */}
      <section className="bg-light-amiso">
        <Container>
          <Fade triggerOnce direction="down">
            <span className="text-divider d-block text-center mb-3">Our Testimonials</span>
            <h2 className="section-title text-center text-white mb-5">What Our Client Says</h2>
          </Fade>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Fade triggerOnce direction="up">
                <div className="amiso-card text-center p-5 bg-dark position-relative">
                    {/* Placeholder for the user avatar carousel (complex for static, simplifying to single image or just text) */}
                    <div className="mb-4" style={{ height: '80px', width: '80px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto', border: '3px solid var(--thm-base)' }}>
                        <img src="/assets/about-us/test-1.jpg" alt="Webbloom Client" className="img-fluid" />
                    </div>
                  <FaQuoteRight size={30} className=" mb-4" />
                  <p className="lead text-white-75 mb-4 px-lg-4">
                    “Webbloom delivered exactly what we needed — a fast, modern, and professional website. Their team was responsive, skilled, and easy to work with. Highly recommended for web and app development.”
                  </p>
                  <h5 className="fw-bold text-white mb-1">Ankit Jain</h5>
                  <p className=" small text-uppercase fw-bold">Business Owner</p>
                  <div className="text-warning mt-3">★★★★★</div> {/* Static stars */}
                </div>
              </Fade>
            </Col>
          </Row>
        </Container>
      </section>

       

      {/* ==================== 11. Contact Form Section ==================== */}
      <section className="bg-dark">
        <div className="overlay" style={{ background: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url("") no-repeat center center', backgroundSize: 'cover', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}></div>
        <Container className="position-relative" style={{ zIndex: 2 }}>
          <Row className="align-items-center">
            <Col lg={5} className="text-center text-lg-start mb-5 mb-lg-0">
              <Fade triggerOnce direction="left">
                <span className="text-divider d-block mb-3">Get in touch</span>
                <h2 className="section-title text-white mb-4">
                  Let’s Connect & Build Something Great
                </h2>
                <p className="section-subtitle text-muted mb-5">
                  Have a project in mind? Reach out to us and let’s create powerful web and app solutions for your business.
                </p>
                <div className="bg-primary p-4 rounded-3 d-inline-block text-start">
                  <h5 className="text-white fw-bold mb-2">Timings</h5>
                  <p className="text-white mb-0">10:00 am - 6:00 pm</p>
                </div>
              </Fade>
            </Col>
            <Col lg={7}>
              <Slide triggerOnce direction="right">
                <Form className="amiso-card p-5" onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter Name" className="border-0 py-3" required />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter Email" className="border-0 py-3" required />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="Enter Subject" className="border-0 py-3" required />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter Phone" className="border-0 py-3" required/>
                    </Col>
                  </Row>
                  <Form.Group className="mb-4">
                    <Form.Control as="textarea" name="message" value={formData.message} onChange={handleChange} rows={6} placeholder="Enter Message" className="border-0 py-3"/>
                  </Form.Group>
                  {errorMessage && (
                    <div className="alert alert-danger mb-4" role="alert">
                      {errorMessage}
                    </div>
                  )}
                  {successMessage && (
                    <div className="alert alert-success mb-4" role="alert">
                      {successMessage}
                    </div>
                  )}
                  <div className="d-flex">
                    <Button variant="primary" type="submit" className="me-3 px-4 py-3" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit'}</Button>
                    <Button variant="outline-light" type="button" onClick={handleReset} className="px-4 py-3">Reset</Button>
                  </div>
                </Form>
              </Slide>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default HomePage;